import { createAsyncThunk, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "app/realtime-store/redux-store";
import type Guid from "common/values/guid/guid";
import LegalEntityAPIService from "legal-entities/entities/legal-entity/api/legal-entity-api-service";
import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import { useSelector } from "react-redux";
import Session from "users/session/session";

// State shape for legal entities
interface LegalEntityStoreState {
  entries: Record<string, LegalEntity[]>;
  loading: Record<string, boolean>;
  error: Record<string, SerializedError | null>;
}

const initialState: LegalEntityStoreState = {
  entries: {},
  loading: {},
  error: {},
};

export const populateLegalEntities = createAsyncThunk<
  LegalEntity[],
  { session: Session; userId: Guid }
>(
  "legalEntities/getLegalEntitiesByUser",
  async ({ session, userId }, thunkAPI) => {
    try {
      const apiService = new LegalEntityAPIService(session);
      return await apiService.getUserLegalEntities(userId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const legalEntitySlice = createSlice({
  name: "legalEntities",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(populateLegalEntities.pending, (state, action) => {
      const userId = action.meta.arg.userId.value;
      state.loading[userId] = true;
      state.error[userId] = null;
    });
    builder.addCase(populateLegalEntities.fulfilled, (state, action) => {
      const userId = action.meta.arg.userId.value;
      state.loading[userId] = false;
      state.entries[userId] = action.payload;
    });
    builder.addCase(populateLegalEntities.rejected, (state, action) => {
      const userId = action.meta.arg.userId.value;
      state.loading[userId] = false;
      state.error[userId] = action.payload as SerializedError;
    });
  },
});

export const { reducer: legalEntityReducer } = legalEntitySlice;

export const getUserLegalEntitiesByUserId = (userId: Guid) => {
  return useSelector(
    (state: RootState) => state.legalEntities?.entries[userId.value]
  );
};
